import React, { useEffect, useState } from "react";
import Product from "../../components/Product";
import { useParams } from "react-router-dom";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { ApiCallStatus } from "../../utils/constants";
import { fetchProductBySku } from "../PageFunctions/productFunctions";
import { fetchProductStockInfo } from "../PageFunctions/priceFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import {
  fetchApplicationMakes,
  fetchApplicationModels,
  fetchApplicationVariants,
  fetchApplicationYears,
} from "../PageFunctions/vehicleFunctions";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import useSystemHook from "../../hooks/SystemHook";


const ProductPage = ({ genericDataLoaded, genericPageContent }) => {
  const { isAuthenticated, isLoading } = useArgusAuth();
  const { productSku } = useParams();
  const [productData, setProductData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);

const [applicationMakes, setApplicationMakes] = useState([]);
  const [applicationModels, setApplicationModels] = useState([]);
  const [applicationVariants, setApplicationVariants] = useState([]);
  const [applicationYears, setApplicationYears] = useState([]);
  const [applicationFilters, setApplicationFilters] = useState({
    makeId: null,
    modelId: null,
    variantId: null,
  });
  const [authError, setAuthError] = useState(true);

  const [productFetchCallStatus, setProductFetchCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [priceFetchCallStatus, setPriceFetchCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [stockInfoCallStatus, setStockInfoCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [fetchApplicationMakesCallStatus, setFetchApplicationMakesCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [
    fetchApplicationModelsCallStatus,
    setFetchApplicationModelsCallStatus,
  ] = useState(ApiCallStatus.NotStarted);
  const [
    fetchApplicationVariantsCallStatus,
    setFetchApplicationVariantsCallStatus,
  ] = useState(ApiCallStatus.NotStarted);
  const [fetchApplicationYearsCallStatus, setFetchApplicationYearsCallStatus] =
    useState(ApiCallStatus.NotStarted);


  const fetchProductBySkuAsync = async () => {
    if (productFetchCallStatus === ApiCallStatus.NotStarted) {
      await fetchProductBySku({
        productSku,
        setProductData,
        setStatusInProgress: () =>
          setProductFetchCallStatus(ApiCallStatus.InProgress),
        setStatusSuccess: () =>
          setProductFetchCallStatus(ApiCallStatus.Succeeded),
        setStatusFailed: () => setProductFetchCallStatus(ApiCallStatus.Failed),
        setStatusError: () => setProductFetchCallStatus(ApiCallStatus.Error),
      });
    }
  };

  const fetchProductStockInfoAsync = async () => {
    const productSkus = [productSku];
    await fetchProductStockInfo({
      productSkus,
      setPricingData,
      setQuantities: setStockInfo,
      setStatusInProgress: () =>
        setStockInfoCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () => setStockInfoCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setStockInfoCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setStockInfoCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchApplicationMakesAsync = async () => {
    await fetchApplicationMakes({
      sku: productSku,
      setApplicationMakes,
      setStatusInProgress: () =>
        setFetchApplicationMakesCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchApplicationMakesCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchApplicationMakesCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchApplicationMakesCallStatus(ApiCallStatus.Error),
      setAuthError: (value) => setAuthError(value),
    });
  };

  const fetchApplicationModelsAsync = async () => {
    await fetchApplicationModels({
      sku: productSku,
      makeId: applicationFilters.makeId,
      setApplicationModels,
      setStatusInProgress: () =>
        setFetchApplicationModelsCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchApplicationModelsCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchApplicationModelsCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchApplicationModelsCallStatus(ApiCallStatus.Error),
      setAuthError: (value) => setAuthError(value),
    });
  };

  const fetchApplicationVariantsAsync = async () => {
    await fetchApplicationVariants({
      sku: productSku,
      makeId: applicationFilters.makeId,
      modelId: applicationFilters.modelId,
      setApplicationVariants,
      setStatusInProgress: () =>
        setFetchApplicationVariantsCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchApplicationVariantsCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchApplicationVariantsCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchApplicationVariantsCallStatus(ApiCallStatus.Error),
      setAuthError: (value) => setAuthError(value),
    });
  };

  const fetchApplicationYearsAsync = async () => {
    await fetchApplicationYears({
      sku: productSku,
      makeId: applicationFilters.makeId,
      modelId: applicationFilters.modelId,
      variantId: applicationFilters.variantId,
      setApplicationYears,
      setStatusInProgress: () =>
        setFetchApplicationYearsCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchApplicationYearsCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchApplicationYearsCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchApplicationYearsCallStatus(ApiCallStatus.Error),
      setAuthError: (value) => setAuthError(value),
    });
  };

  const unauthorizedCalls = async () => {
    await Promise.all([fetchProductBySkuAsync()]);
  };

  const authorizedCalls = async () => {
    await Promise.all([fetchApplicationMakesAsync()]);
  };

  useEffect(() => {
    if (genericDataLoaded === true && isLoading === false) {
      if (isAuthenticated === false) {
        unauthorizedCalls();
      } else {
        authorizedCalls();
        unauthorizedCalls();
      }
    }
  }, [genericDataLoaded, isAuthenticated, isLoading]);

  useEffect(() => {
    if (productFetchCallStatus === ApiCallStatus.Succeeded) {
      fetchProductStockInfoAsync();
    }
  }, [productFetchCallStatus]);

  useEffect(() => {
    if (isAuthenticated === true && !!applicationFilters) {
      if (!applicationFilters.makeId) {
        fetchApplicationMakesAsync();
      } else if (!!applicationFilters.makeId && !applicationFilters.modelId) {
        fetchApplicationModelsAsync();
      } else if (
        !!applicationFilters.makeId &&
        !!applicationFilters.modelId &&
        !applicationFilters.variantId
      ) {
        fetchApplicationVariantsAsync();
      } else if (
        !!applicationFilters.makeId &&
        !!applicationFilters.modelId &&
        !!applicationFilters.variantId
      ) {
        fetchApplicationYearsAsync();
      }
    }
  }, [isAuthenticated, applicationFilters]);

  useEffect(() => {
    if (fetchApplicationMakesCallStatus === ApiCallStatus.Succeeded) {
      setApplicationYears([]);
      setApplicationModels([]);
      setApplicationVariants([]);
    }
  }, [fetchApplicationMakesCallStatus]);

  useEffect(() => {
    if (fetchApplicationModelsCallStatus === ApiCallStatus.Succeeded) {
      setApplicationYears([]);
      setApplicationVariants([]);
    }
  }, [fetchApplicationModelsCallStatus]);

  useEffect(() => {
    if (fetchApplicationVariantsCallStatus === ApiCallStatus.Succeeded) {
      setApplicationYears([]);
    }
  }, [fetchApplicationVariantsCallStatus]);

  const allLoadingStates = [
    productFetchCallStatus,
    priceFetchCallStatus,
    stockInfoCallStatus,
  ];

  const applicationTabLoadingStates = [
    fetchApplicationMakesCallStatus,
    fetchApplicationModelsCallStatus,
    fetchApplicationVariantsCallStatus,
    fetchApplicationYearsCallStatus,
  ];

  const pageIsLoading =
    allLoadingStates.includes(ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;
  const applicationTabIsLoading =
    applicationTabLoadingStates.includes(ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;

  const { isMobile } = useSystemHook();

  return (
    <Container maxWidth="lg">
      <Breadcrumbs />
      <Product
        product={productData}
        pricingData={pricingData}
        stockInfo={stockInfo}
        applicationMakes={applicationMakes}
        applicationModels={applicationModels}
        applicationVariants={applicationVariants}
        applicationYears={applicationYears}
        applicationFilters={applicationFilters}
        setApplicationFilters={setApplicationFilters}
        accountType={genericPageContent?.accountType}
        loading={pageIsLoading}
        applicationTabIsLoading={applicationTabIsLoading}
        isAuthenticated={isAuthenticated}
        authError={authError}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={pageIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
export default ProductPage;
